import { supabase } from "../supabaseClient";

export interface League {
  id: string;
  user_id: string;
  name: string;
  format: "INDOOR" | "T20" | "ODI";
  overs: number;
  start_date: string | null;
  end_date: string | null;
  location: string | null;
  competition_type?: string | null;
  points_win?: number;
  points_tie?: number;
  points_no_result?: number;
  created_at: string;
}

export interface LeagueTeam {
  id: string;
  league_id: string;
  team_id: string | null;
  team_name: string;
  team_logo_url: string | null;
  team_icon_id: string | null;
  captain: string | null;
  vice_captain: string | null;
  wicket_keeper: string | null;
  team_color: string | null;
  notes: string | null;
  created_at: string;
}

export interface LeagueFixture {
  id: string;
  league_id: string;
  season_id?: string | null;
  round_number?: number | null;
  fixture_order?: number | null;
  team_a_id?: string | null;
  team_a_name: string;
  team_b_id?: string | null;
  team_b_name: string;
  scheduled_at?: string | null;
  match_id?: string | null;
  status: string;
  result_text?: string | null;
  venue?: string | null;
  notes?: string | null;
  created_at: string;
  updated_at?: string;
  home_team_id?: string;
  away_team_id?: string;
  round?: string | null;
  match_date?: string;
  match_time?: string;
  home_team_name?: string;
  away_team_name?: string;
}

export interface LeagueTableEntry {
  id: string;
  league_id: string;
  team_id: string | null;
  team_name: string;
  played: number;
  won: number;
  lost: number;
  tied: number;
  no_result: number;
  points: number;
  updated_at?: string;
}

export interface LeagueStanding {
  id: string;
  league_id: string;
  team_id: string;
  team_name?: string;
  matches_played: number;
  wins: number;
  losses: number;
  ties: number;
  no_results: number;
  points: number;
  runs_for: number;
  runs_against: number;
  overs_faced: number;
  overs_bowled: number;
  net_run_rate: number;
  updated_at: string;
}

// League CRUD operations
export async function listLeagues(): Promise<League[]> {
  const { data, error } = await supabase
    .from("leagues")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) {
    console.error("Error fetching leagues:", error);
    return [];
  }

  return data || [];
}

export async function getLeague(leagueId: string): Promise<League | null> {
  const { data, error } = await supabase
    .from("leagues")
    .select("*")
    .eq("id", leagueId)
    .maybeSingle();

  if (error) {
    console.error("Error fetching league:", error);
    return null;
  }

  return data;
}

export async function createLeague(league: Omit<League, "id" | "user_id" | "created_at">): Promise<League | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from("leagues")
    .insert({
      ...league,
      user_id: user.id,
    })
    .select()
    .single();

  if (error) {
    console.error("Error creating league:", error);
    return null;
  }

  return data;
}

export async function updateLeague(leagueId: string, updates: Partial<League>): Promise<boolean> {
  const { error } = await supabase
    .from("leagues")
    .update(updates)
    .eq("id", leagueId);

  if (error) {
    console.error("Error updating league:", error);
    return false;
  }

  return true;
}

export async function deleteLeague(leagueId: string): Promise<boolean> {
  const { error } = await supabase
    .from("leagues")
    .delete()
    .eq("id", leagueId);

  if (error) {
    console.error("Error deleting league:", error);
    return false;
  }

  return true;
}

// League Teams operations
export async function getLeagueTeams(leagueId: string): Promise<LeagueTeam[]> {
  const { data, error } = await supabase
    .from("league_teams")
    .select("*")
    .eq("league_id", leagueId);

  if (error) {
    console.error("Error fetching league teams:", error);
    return [];
  }

  return data || [];
}

export async function addTeamToLeague(
  leagueId: string,
  teamName: string,
  options?: {
    teamLogoUrl?: string | null;
    teamIconId?: string | null;
    captain?: string | null;
    viceCaptain?: string | null;
    wicketKeeper?: string | null;
    teamColor?: string | null;
    notes?: string | null;
  }
): Promise<boolean> {
  const { data, error } = await supabase
    .from("league_teams")
    .insert({
      league_id: leagueId,
      team_name: teamName,
      team_logo_url: options?.teamLogoUrl || null,
      team_icon_id: options?.teamIconId || null,
      captain: options?.captain || null,
      vice_captain: options?.viceCaptain || null,
      wicket_keeper: options?.wicketKeeper || null,
      team_color: options?.teamColor || null,
      notes: options?.notes || null,
      team_id: null,
    })
    .select();

  if (error) {
    console.error("[DEV] Error adding team to league:", error);
    return false;
  }

  return true;
}

export async function updateLeagueTeam(
  teamRecordId: string,
  updates: {
    team_name?: string;
    team_logo_url?: string | null;
    team_icon_id?: string | null;
    captain?: string | null;
    vice_captain?: string | null;
    notes?: string | null;
  }
): Promise<boolean> {
  const { error } = await supabase
    .from("league_teams")
    .update(updates)
    .eq("id", teamRecordId);

  if (error) {
    console.error("[DEV] Error updating league team:", error);
    return false;
  }
  return true;
}

export async function removeTeamFromLeague(leagueId: string, teamRecordId: string): Promise<boolean> {
  console.log('[DEV] removeTeamFromLeague:', { leagueId, teamRecordId });

  const { error } = await supabase
    .from("league_teams")
    .delete()
    .eq("id", teamRecordId);

  if (error) {
    console.error("[DEV] Error removing team from league:", error);
    return false;
  }

  console.log('[DEV] Team removed successfully');
  return true;
}

// League Fixtures operations
export async function getLeagueFixtures(leagueId: string): Promise<LeagueFixture[]> {
  const { data, error } = await supabase
    .from("league_fixtures")
    .select("*")
    .eq("league_id", leagueId)
    .order("round_number", { ascending: true, nullsFirst: false })
    .order("fixture_order", { ascending: true, nullsFirst: false })
    .order("scheduled_at", { ascending: true, nullsFirst: false });

  if (error) {
    console.error("Error fetching league fixtures:", error);
    return [];
  }

  return data || [];
}

export async function generateLeagueFixtures(
  leagueId: string,
  fixtures: Array<{
    round_number: number;
    fixture_order: number;
    team_a_id: string | null;
    team_a_name: string;
    team_b_id: string | null;
    team_b_name: string;
  }>
): Promise<boolean> {
  console.log("[DEV] generateLeagueFixtures:", { leagueId, fixtureCount: fixtures.length });

  const fixtureRecords = fixtures.map((f) => ({
    league_id: leagueId,
    round_number: f.round_number,
    fixture_order: f.fixture_order,
    team_a_id: f.team_a_id,
    team_a_name: f.team_a_name,
    team_b_id: f.team_b_id,
    team_b_name: f.team_b_name,
    status: "upcoming",
  }));

  const { data, error } = await supabase
    .from("league_fixtures")
    .insert(fixtureRecords)
    .select();

  if (error) {
    console.error("[DEV] Error generating fixtures:", error);
    return false;
  }

  console.log("[DEV] Fixtures generated successfully:", data?.length);
  return true;
}

export async function deleteAllLeagueFixtures(leagueId: string): Promise<boolean> {
  console.log("[DEV] deleteAllLeagueFixtures:", { leagueId });

  const { error } = await supabase
    .from("league_fixtures")
    .delete()
    .eq("league_id", leagueId);

  if (error) {
    console.error("[DEV] Error deleting fixtures:", error);
    return false;
  }

  console.log("[DEV] All fixtures deleted");
  return true;
}

export async function createFixture(fixture: Omit<LeagueFixture, "id" | "created_at" | "home_team_name" | "away_team_name">): Promise<LeagueFixture | null> {
  const { data, error } = await supabase
    .from("league_fixtures")
    .insert(fixture)
    .select()
    .single();

  if (error) {
    console.error("Error creating fixture:", error);
    return null;
  }

  return data;
}

export async function updateFixture(fixtureId: string, updates: Partial<LeagueFixture>): Promise<boolean> {
  const { error } = await supabase
    .from("league_fixtures")
    .update(updates)
    .eq("id", fixtureId);

  if (error) {
    console.error("Error updating fixture:", error);
    return false;
  }

  return true;
}

export async function deleteFixture(fixtureId: string): Promise<boolean> {
  const { error } = await supabase
    .from("league_fixtures")
    .delete()
    .eq("id", fixtureId);

  if (error) {
    console.error("Error deleting fixture:", error);
    return false;
  }

  return true;
}

// League Standings operations
export async function getLeagueStandings(leagueId: string): Promise<LeagueStanding[]> {
  const { data, error } = await supabase
    .from("league_standings")
    .select("*")
    .eq("league_id", leagueId)
    .order("points", { ascending: false })
    .order("net_run_rate", { ascending: false });

  if (error) {
    console.error("Error fetching league standings:", error);
    return [];
  }

  return data || [];
}

export async function updateLeagueStandings(
  leagueId: string,
  teamId: string,
  matchResult: {
    won: boolean;
    tied: boolean;
    noResult: boolean;
    runsScored: number;
    runsConceded: number;
    oversFaced: number;
    oversBowled: number;
  }
): Promise<boolean> {
  // Get or create standing
  const { data: existing } = await supabase
    .from("league_standings")
    .select("*")
    .eq("league_id", leagueId)
    .eq("team_id", teamId)
    .maybeSingle();

  const current = existing || {
    matches_played: 0,
    wins: 0,
    losses: 0,
    ties: 0,
    no_results: 0,
    points: 0,
    runs_for: 0,
    runs_against: 0,
    overs_faced: 0,
    overs_bowled: 0,
  };

  // Calculate new values
  const newMatchesPlayed = current.matches_played + 1;
  const newWins = current.wins + (matchResult.won ? 1 : 0);
  const newLosses = current.losses + (!matchResult.won && !matchResult.tied && !matchResult.noResult ? 1 : 0);
  const newTies = current.ties + (matchResult.tied ? 1 : 0);
  const newNoResults = current.no_results + (matchResult.noResult ? 1 : 0);

  // Points: Win = 2, Tie/NR = 1, Loss = 0
  let pointsGained = 0;
  if (matchResult.won) pointsGained = 2;
  else if (matchResult.tied || matchResult.noResult) pointsGained = 1;

  const newPoints = current.points + pointsGained;
  const newRunsFor = current.runs_for + matchResult.runsScored;
  const newRunsAgainst = current.runs_against + matchResult.runsConceded;
  const newOversFaced = parseFloat(current.overs_faced.toString()) + matchResult.oversFaced;
  const newOversBowled = parseFloat(current.overs_bowled.toString()) + matchResult.oversBowled;

  // Calculate NRR: (runs_for / overs_faced) - (runs_against / overs_bowled)
  let newNRR = 0;
  if (newOversFaced > 0 && newOversBowled > 0) {
    newNRR = (newRunsFor / newOversFaced) - (newRunsAgainst / newOversBowled);
  }

  const updatedData = {
    league_id: leagueId,
    team_id: teamId,
    matches_played: newMatchesPlayed,
    wins: newWins,
    losses: newLosses,
    ties: newTies,
    no_results: newNoResults,
    points: newPoints,
    runs_for: newRunsFor,
    runs_against: newRunsAgainst,
    overs_faced: newOversFaced,
    overs_bowled: newOversBowled,
    net_run_rate: newNRR,
    updated_at: new Date().toISOString(),
  };

  const { error } = await supabase
    .from("league_standings")
    .upsert(updatedData);

  if (error) {
    console.error("Error updating league standings:", error);
    return false;
  }

  return true;
}

// League Table Entries operations
export async function getLeagueTableEntries(leagueId: string): Promise<LeagueTableEntry[]> {
  console.log("[DEV] getLeagueTableEntries:", { leagueId });

  const { data, error } = await supabase
    .from("league_table_entries")
    .select("*")
    .eq("league_id", leagueId)
    .order("points", { ascending: false })
    .order("won", { ascending: false })
    .order("team_name", { ascending: true });

  if (error) {
    console.error("[DEV] Error fetching league table entries:", error);
    return [];
  }

  console.log("[DEV] Found table entries:", data?.length || 0);
  return data || [];
}

export async function initializeLeagueTable(leagueId: string): Promise<boolean> {
  console.log("[DEV] initializeLeagueTable:", { leagueId });

  const existingEntries = await getLeagueTableEntries(leagueId);
  if (existingEntries.length > 0) {
    console.log("[DEV] Table already initialized with", existingEntries.length, "entries");
    return true;
  }

  const teams = await getLeagueTeams(leagueId);
  if (teams.length === 0) {
    console.log("[DEV] No teams found, cannot initialize table");
    return false;
  }

  const tableEntries = teams.map((team) => ({
    league_id: leagueId,
    team_id: team.id,
    team_name: team.team_name,
    played: 0,
    won: 0,
    lost: 0,
    tied: 0,
    no_result: 0,
    points: 0,
  }));

  const { error } = await supabase
    .from("league_table_entries")
    .insert(tableEntries);

  if (error) {
    console.error("[DEV] Error initializing league table:", error);
    return false;
  }

  console.log("[DEV] League table initialized with", tableEntries.length, "entries");
  return true;
}

export async function updateLeagueTableEntry(
  leagueId: string,
  teamId: string,
  updates: {
    won?: number;
    lost?: number;
    tied?: number;
    no_result?: number;
  }
): Promise<boolean> {
  console.log("[DEV] updateLeagueTableEntry:", { leagueId, teamId, updates });

  const entries = await getLeagueTableEntries(leagueId);
  const entry = entries.find((e) => e.team_id === teamId);

  if (!entry) {
    console.error("[DEV] Table entry not found for team:", teamId);
    return false;
  }

  const league = await supabase
    .from("leagues")
    .select("points_win, points_tie, points_no_result")
    .eq("id", leagueId)
    .single();

  const pointsWin = league.data?.points_win || 2;
  const pointsTie = league.data?.points_tie || 1;
  const pointsNoResult = league.data?.points_no_result || 1;

  const newWon = entry.won + (updates.won || 0);
  const newLost = entry.lost + (updates.lost || 0);
  const newTied = entry.tied + (updates.tied || 0);
  const newNoResult = entry.no_result + (updates.no_result || 0);
  const newPlayed = newWon + newLost + newTied + newNoResult;

  const newPoints = (newWon * pointsWin) + (newTied * pointsTie) + (newNoResult * pointsNoResult);

  const { error } = await supabase
    .from("league_table_entries")
    .update({
      played: newPlayed,
      won: newWon,
      lost: newLost,
      tied: newTied,
      no_result: newNoResult,
      points: newPoints,
      updated_at: new Date().toISOString(),
    })
    .eq("id", entry.id);

  if (error) {
    console.error("[DEV] Error updating league table entry:", error);
    return false;
  }

  console.log("[DEV] Table entry updated successfully");
  return true;
}
